/**
 * "Copy data to…": a table's rows into another table with the same columns,
 * in bulk (owner, 30.09.2026: one click, no per-row requests).
 *
 * Host-side on purpose: the guest's db.query is ONE prepared statement with no
 * transactions, so batches and INSERT … SELECT stay out of the sandbox. The
 * DB Manager calls the id-gated `db.copyTableData` (hostBridge); everything
 * here acts as that extension on its own connections.
 *
 *   same connection   → ONE batch: [DELETE target,] INSERT … SELECT. Server-side
 *                       and atomic; no rows travel through the app.
 *   other connection  → same engine only (v1). Keyset pages on a one-column
 *                       primary key (OFFSET with a fixed order otherwise); each
 *                       page is ONE multi-row INSERT through the atomic batch
 *                       command — one round trip per chunk, never per row.
 *
 * The column sets must match (same names; same types after normalising);
 * generated columns are skipped. Nothing is written before that check.
 * "Empty target first" is a DELETE in the same transaction (TRUNCATE would
 * commit implicitly on MySQL and break the all-or-nothing).
 */
import { DB_QUERY_COMMANDS, DB_BATCH_COMMANDS, quoteIdentFor, makePlaceholder } from './dbEngines';
import { pgCastFor } from '../components/dbGridSql';

// Bound-parameter ceilings per statement (MySQL/PG 65535; SQLite ≥ 3.32 32766).
const PARAM_LIMIT = { mysql: 65535, postgres: 65535, sqlite: 32766 };

export function chunkSizeFor(engine, columnCount) {
  const cols = Math.max(1, columnCount);
  return Math.max(1, Math.min(1000, Math.floor((PARAM_LIMIT[engine] || 999) / cols)));
}

export function tableRef(engine, { db, table }) {
  const t = quoteIdentFor(engine, table);
  if (engine === 'sqlite' || !db) return t;
  return `${quoteIdentFor(engine, db)}.${t}`;
}

const normType = (t) => String(t || '').toLowerCase().replace(/\s+/g, ' ').replace(/ unsigned$/, ' unsigned').trim();

/** [{ name, type, generated, pk, udtName, udtSchema }] in table order. */
export async function readColumns(run, engine, connId, { db, table }) {
  if (engine === 'postgres') {
    const res = await run(DB_QUERY_COMMANDS.postgres, connId,
      'SELECT c.column_name, c.data_type, c.is_generated, c.udt_name, c.udt_schema, ' +
      "(EXISTS (SELECT 1 FROM information_schema.table_constraints tc JOIN information_schema.key_column_usage k " +
      "ON k.constraint_name = tc.constraint_name AND k.table_schema = tc.table_schema " +
      "WHERE tc.constraint_type = 'PRIMARY KEY' AND tc.table_schema = c.table_schema AND tc.table_name = c.table_name AND k.column_name = c.column_name)) AS pk " +
      'FROM information_schema.columns c WHERE c.table_schema = $1 AND c.table_name = $2 ORDER BY c.ordinal_position',
      [db || 'public', table]);
    return res.rows.map((r) => ({ name: r[0], type: r[1], generated: r[2] === 'ALWAYS', udtName: r[3], udtSchema: r[4], pk: r[5] === true || r[5] === 't' }));
  }
  if (engine === 'mysql') {
    const res = await run(DB_QUERY_COMMANDS.mysql, connId,
      'SELECT COLUMN_NAME, COLUMN_TYPE, EXTRA, COLUMN_KEY FROM information_schema.COLUMNS WHERE TABLE_SCHEMA = ? AND TABLE_NAME = ? ORDER BY ORDINAL_POSITION',
      [db, table]);
    return res.rows.map((r) => ({ name: r[0], type: r[1], generated: /GENERATED/i.test(String(r[2] || '')), pk: r[3] === 'PRI' }));
  }
  const res = await run(DB_QUERY_COMMANDS.sqlite, connId, `PRAGMA table_info(${quoteIdentFor('sqlite', table)})`, []);
  const idx = (n) => res.columns.indexOf(n);
  return res.rows.map((r) => ({ name: r[idx('name')], type: r[idx('type')], generated: false, pk: Number(r[idx('pk')]) > 0 }));
}

/** null when copyable, else a readable list of differences. */
export function compareColumns(sourceCols, targetCols) {
  const src = sourceCols.filter((c) => !c.generated);
  const tgt = new Map(targetCols.filter((c) => !c.generated).map((c) => [c.name, c]));
  const problems = [];
  for (const c of src) {
    const t = tgt.get(c.name);
    if (!t) problems.push(`"${c.name}" is missing in the target`);
    else if (normType(t.type) !== normType(c.type)) problems.push(`"${c.name}": ${c.type} → ${t.type}`);
    tgt.delete(c.name);
  }
  for (const name of tgt.keys()) problems.push(`"${name}" exists only in the target`);
  return problems.length ? problems : null;
}

function insertVerb(engine, onConflict) {
  if (onConflict !== 'skip') return 'INSERT INTO';
  if (engine === 'mysql') return 'INSERT IGNORE INTO';
  if (engine === 'sqlite') return 'INSERT OR IGNORE INTO';
  return 'INSERT INTO';
}
const conflictTail = (engine, onConflict) => (engine === 'postgres' && onConflict === 'skip' ? ' ON CONFLICT DO NOTHING' : '');

/** The statements for a same-connection copy — one batch. */
export function planSameConnection(engine, source, target, columns, { emptyFirst, onConflict }) {
  const cols = columns.map((c) => quoteIdentFor(engine, c.name)).join(', ');
  const statements = [];
  if (emptyFirst) statements.push({ sql: `DELETE FROM ${tableRef(engine, target)}`, params: [] });
  statements.push({
    sql: `${insertVerb(engine, onConflict)} ${tableRef(engine, target)} (${cols}) SELECT ${cols} FROM ${tableRef(engine, source)}${conflictTail(engine, onConflict)}`,
    params: [],
  });
  return statements;
}

/** One multi-row INSERT for a page of rows (postgres params get their column cast). */
export function planInsertChunk(engine, target, columns, rows, { onConflict }) {
  const ph = makePlaceholder(engine);
  const casts = columns.map((c) => (engine === 'postgres' ? pgCastFor({ type: c.type, udtName: c.udtName, udtSchema: c.udtSchema }) : ''));
  const params = [];
  const values = rows.map((row) => `(${row.map((v, i) => { params.push(v); return `${ph()}${casts[i]}`; }).join(', ')})`);
  const cols = columns.map((c) => quoteIdentFor(engine, c.name)).join(', ');
  return {
    sql: `${insertVerb(engine, onConflict)} ${tableRef(engine, target)} (${cols}) VALUES ${values.join(', ')}${conflictTail(engine, onConflict)}`,
    params,
  };
}

async function countRows(run, engine, connId, ref) {
  const res = await run(DB_QUERY_COMMANDS[engine], connId, `SELECT COUNT(*) FROM ${tableRef(engine, ref)}`, []);
  return Number(res.rows?.[0]?.[0]) || 0;
}

/**
 * Run a copy. `call(command, args)` is invokeAsExtension-shaped; `extensionId`
 * is the DB Manager's. Resolves { copied, mode: 'same' | 'chunked', chunks }.
 * `onProgress({ copied, total })` and `isCancelled()` are optional (chunked).
 */
export async function copyTableData({ call, extensionId, source, target, options = {}, onStart, onProgress, isCancelled }) {
  if (source.engine !== target.engine) throw new Error('Copying between different database engines is not supported yet.');
  const engine = source.engine;
  if (source.connId === target.connId && (source.db || '') === (target.db || '') && source.table === target.table) {
    throw new Error('Source and target are the same table.');
  }
  const run = (command, connId, sql, params) => call(command, { extensionId, connId, sql, params });
  const batch = (connId, statements) => call(DB_BATCH_COMMANDS[engine], { extensionId, connId, statements });

  const [srcCols, tgtCols] = await Promise.all([
    readColumns(run, engine, source.connId, source),
    readColumns(run, engine, target.connId, target),
  ]);
  if (!srcCols.length) throw new Error(`Source table ${source.table} has no columns (or does not exist).`);
  if (!tgtCols.length) throw new Error(`Target table ${target.table} has no columns (or does not exist).`);
  const diff = compareColumns(srcCols, tgtCols);
  if (diff) throw new Error(`The tables do not have the same columns: ${diff.join('; ')}`);
  const columns = srcCols.filter((c) => !c.generated).map((c) => ({ ...c, ...(tgtCols.find((t) => t.name === c.name) || {}) }));

  const before = options.emptyFirst ? 0 : await countRows(run, engine, target.connId, target);
  // Checks passed; nothing written yet. The bridge answers the guest here.
  onStart?.({ columns: columns.length, sameConnection: source.connId === target.connId });

  if (source.connId === target.connId) {
    await batch(target.connId, planSameConnection(engine, source, target, columns, options));
    const after = await countRows(run, engine, target.connId, target);
    return { copied: after - before, mode: 'same', chunks: 1 };
  }

  // Across connections: keyset on a single-column PK, else ordered OFFSET.
  const total = await countRows(run, engine, source.connId, source);
  const pkCols = srcCols.filter((c) => c.pk);
  const keyset = pkCols.length === 1 ? pkCols[0] : null;
  const size = chunkSizeFor(engine, columns.length);
  const colList = columns.map((c) => quoteIdentFor(engine, c.name)).join(', ');
  const orderBy = keyset ? quoteIdentFor(engine, keyset.name) : (pkCols.length ? pkCols : columns).map((c) => quoteIdentFor(engine, c.name)).join(', ');
  const keyIdx = keyset ? columns.findIndex((c) => c.name === keyset.name) : -1;
  let cursor = null;
  let offset = 0;
  let copiedRows = 0;
  let chunks = 0;
  let first = true;
  for (;;) {
    if (isCancelled?.()) break;
    const ph = makePlaceholder(engine);
    const where = keyset && cursor !== null ? ` WHERE ${quoteIdentFor(engine, keyset.name)} > ${ph()}` : '';
    const sql = `SELECT ${colList} FROM ${tableRef(engine, source)}${where} ORDER BY ${orderBy} LIMIT ${size}${keyset ? '' : ` OFFSET ${offset}`}`;
    // eslint-disable-next-line no-await-in-loop
    const page = await run(DB_QUERY_COMMANDS[engine], source.connId, sql, keyset && cursor !== null ? [cursor] : []);
    const rows = page.rows || [];
    if (rows.length === 0) break;
    const statements = [];
    if (first && options.emptyFirst) statements.push({ sql: `DELETE FROM ${tableRef(engine, target)}`, params: [] });
    statements.push(planInsertChunk(engine, target, columns, rows, options));
    // eslint-disable-next-line no-await-in-loop
    await batch(target.connId, statements);
    first = false;
    chunks += 1;
    copiedRows += rows.length;
    onProgress?.({ copied: copiedRows, total });
    if (rows.length < size) break;
    if (keyset) cursor = rows[rows.length - 1][keyIdx];
    else offset += rows.length;
  }
  const after = await countRows(run, engine, target.connId, target);
  return { copied: after - before, mode: 'chunked', chunks, cancelled: !!isCancelled?.() };
}
